import styled from "@emotion/styled"
import { Box, Divider, Typography } from "@mui/material"
import * as api from "api"
import RatioElement from "common/ElementRatio"
import NextLink from "common/NextLink"
import ResponsiveModelImage from "common/ResponsiveModelImage"
import slugify from "common/utils/slugify"
import ContactUsButton from "components/ContactUs/ContactUsButton"
import { memo } from "react"
import Shared from "theme/shared"
import VehicleMissons, { TagSmall } from "./VehicleMissons"
import VehicleNotices, { LicensingFeeSmall } from "./VehicleNotices"
import VehicleOptions, { OptionsSmall } from "./VehicleOptions"
import VehiclePrice, { PriceSmall } from "./VehiclePrice"

type VehicleModelProps = { model: api.ModelsVehicle }

const Styled = {
  Wrapper: styled.div`
    display: block;
    border-radius: 5px;
    border: solid 1px ${(p) => p.theme.colors.darkIndigo};
    box-shadow: 0 10px 20px 0 ${(p) => p.theme.colors.darkIndigo};
    margin: 65px 15px 0;
    padding: 5px 22px 10px;
    ${(p) => p.theme.breakpoints.up("md")} {
      margin: 90px 33px 0;
      padding: 20px 38px;
    }

    ${TagSmall}
    ${OptionsSmall}
    ${PriceSmall}
    ${LicensingFeeSmall}
  `,
  Image: styled.div`
    margin: -60px 15px 0;
    ${(p) => p.theme.breakpoints.up("md")} {
      margin: -100px 51px 0;
    }
  `,
  Model: styled(Typography)`
    font-size: 26px;
    font-weight: bold;
    text-transform: uppercase;
    color: ${(p) => p.theme.colors.oceanBlue};
    ${(p) => p.theme.breakpoints.up("md")} {
      margin-bottom: 5px;
      font-size: 30px;
    }
  `,
  Button: styled(Shared.Button)`
    margin-top: 10px;
    font-size: 12px;
    width: 100%;
    ${(p) => p.theme.breakpoints.up("lg")} {
      font-size: 18px;
      margin: 0 0 15px;
    }
  `,
  Grid: styled.div`
    margin-top: 15px;
    display: flex;
    flex-direction: column;
    ${(p) => p.theme.breakpoints.up("lg")} {
      gap: 35px;
      flex-direction: row;
    }
  `,
  ButtonsGrid: styled.div`
    flex: 1;
  `,
  PriceGrid: styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
    ${(p) => p.theme.breakpoints.up("lg")} {
      flex-direction: column;
    }
  `,
  Divider: styled(Divider)`
    height: 2px;
    margin: 0;
    background-color: ${(p) => p.theme.colors.divider};
  `,
}
const VehicleModel = ({ model }: VehicleModelProps) => {
  const link = `/${model.id}/${slugify(model.name)}`

  return (
    <Styled.Wrapper>
      <Styled.Image>
        <RatioElement ratio={17 / 11}>
          <NextLink href={link}>
            <ResponsiveModelImage
              image={model.image}
              imageSizes={["70vw", "395px"]}
              transparent
            />
          </NextLink>
        </RatioElement>
      </Styled.Image>
      <Styled.Model>
        <NextLink href={link}>{model.name}</NextLink>
      </Styled.Model>
      <VehicleMissons data={model.missions} />
      <VehicleOptions data={model.mainOptions} />
      <Styled.Divider />
      <Styled.Grid>
        <Styled.PriceGrid>
          {model.displayPriceWithoutVat && (
            <VehiclePrice price={model.priceWithoutVat} vat={false} />
          )}
          <VehiclePrice price={model.priceWithVat} vat={true} />
        </Styled.PriceGrid>
        <Styled.ButtonsGrid>
          <NextLink href={link} passHref>
            <Styled.Button variant="outlined">גלה עוד פרטים</Styled.Button>
          </NextLink>
          <ContactUsButton
            component={<Styled.Button variant="contained" />}
            modelId={model.id}
          />
        </Styled.ButtonsGrid>
      </Styled.Grid>
      <Box textAlign="right">
        <VehicleNotices type="ModelsVehicle" data={model} />
      </Box>
    </Styled.Wrapper>
  )
}

export default memo(VehicleModel)
